import { onUnmounted, watch } from 'vue';
import type { Ref } from 'vue';
import { useAutopilotScope, useAutopilotStore } from '@llmor/autopilot-vue';
import { formatCompactList } from '@/autopilot/formatCompactList';

/**
 * A single autopilot-drivable list filter. Bundles the JSON-schema that describes it, a getter for
 * its current value, and a setter that applies a new value — keeping the three in lockstep. The
 * setter must mutate something that triggers the list to refetch (e.g. a ref bound to
 * `RemoteListView`'s `searchQuery`/`order`/`parameters`).
 */
export interface AutopilotListFilter {
  /** JSON-schema property: `{ type, enum?, description }`. Becomes one property of `set_filters`. */
  schema: Record<string, any>;
  /** Reads the current value — surfaced in `context[key].filters` and used for unchanged-detection. */
  get: () => unknown;
  /** Writes a new value (mutates the backing ref → triggers the list refetch). */
  set: (value: unknown) => void;
}

/** Loose value equality for filter diffing — handles primitives, arrays and plain objects. */
function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  // treat empty-ish values (null/undefined/'') as equivalent so "clear" no-ops when already clear
  const emptyA = a === null || a === undefined || a === '';
  const emptyB = b === null || b === undefined || b === '';
  if (emptyA && emptyB) return true;
  return JSON.stringify(a) === JSON.stringify(b);
}

export interface AutopilotListConfig {
  /** Reactive autopilot key. When empty the list opts out and nothing is registered. */
  key: () => string | undefined;
  /** Reactive description fed into the LLM context to explain what the list is. */
  description: () => string | undefined;
  /**
   * Maps each loaded item into the row surfaced in context. Object output is flattened into a
   * compact pipe-delimited table (column names appear once) before publishing; string output is
   * newline-joined as-is.
   */
  transformer?: (item: any) => string | Record<string, any>;
  /**
   * Builds a canonical `pjas://` link for an item. When given and the (transformed) item is an
   * object, the link is attached as its `link` field so the LLM can navigate to the row.
   */
  link?: (item: any) => string;
  /** The current page's loaded items. */
  collection: Ref<any[]>;
  /** Current page (1-based). */
  page: Ref<number>;
  /** Total page count. */
  pageCount: Ref<number>;
  /** Filtered total item count. */
  filterCount: Ref<number>;
  /** Unfiltered total item count. */
  totalCount: Ref<number>;
  /** Jumps the list to the given (1-based) page. */
  paginate: (page: number) => void;
  /**
   * The filters the assistant may drive, keyed by name (e.g. `search`, `status`, `sort`). When
   * provided, a single `${key}_set_filters(patch)` function is registered whose parameter schema is
   * assembled from the descriptors. Setting filters resolves only after the list has reloaded and
   * `context[key].items` has been republished — so the assistant never reads stale rows. Current
   * values are published at `context[key].filters`.
   */
  filters?: () => Record<string, AutopilotListFilter>;
}

/**
 * Exposes a paginated list to autopilot: while the owning component is mounted it publishes
 * `context[key]` (description + transformed items + pagination counts) and registers a
 * `${key}_paginate(page)` function. Opt-in — does nothing while `key()` is empty.
 *
 * Context is reassigned as a fresh object on every change because the lib diffs by reference;
 * mutating in place would not sync.
 */
export function useAutopilotList(cfg: AutopilotListConfig): void {
  const store = useAutopilotStore();

  const key = cfg.key();
  const scope = useAutopilotScope();

  // context resolution held while a filter/pagination change is reloading the list. the autopilot
  // client withholds the function response until we release it — which we do once the collection has
  // actually been replaced by the fetch (see the watch below), so the assistant reads the fresh rows.
  let releaseResolution: (() => void) | null = null;
  let collectionAtHold: unknown[] | undefined;
  const holdUntilReload = (label: string) => {
    releaseResolution?.(); // settle any prior hold (idempotent)
    collectionAtHold = cfg.collection.value;
    releaseResolution = store.client.beginContextResolution(label);
  };

  if (key) {
    scope.claimContext(key);
    const functionName = `${key}_paginate`;
    scope.registerFunction({
      name: functionName,
      description:
        `Jump the "${key}" list to a specific page. The resulting items appear in ` +
        `context.${key}.items. Pages are 1-based and bounded by context.${key}.page_count.`,
      parameters: {
        type: 'object',
        properties: {
          page: {
            type: 'integer',
            description: '1-based page number to jump to.',
          },
        },
        required: ['page'],
      },
      handler: async (rawArgs) => {
        const args = (rawArgs ?? {}) as { page?: number };
        const requested = Math.floor(Number(args.page ?? 1));
        const clamped = Math.min(Math.max(1, requested), Math.max(1, cfg.pageCount.value));
        // withhold the response until the new page has loaded and context reflects its rows
        holdUntilReload(functionName);
        cfg.paginate(clamped);
        return { success: true, page: clamped };
      },
    });

    // opt-in filters: lets the assistant drive the list's filter controls (search, status, sort, …).
    // registered only when the owning list declares its filters.
    const hasFilters = !!cfg.filters && Object.keys(cfg.filters()).length > 0;
    if (hasFilters) {
      const setFiltersName = `${key}_set_filters`;
      const names = Object.keys(cfg.filters!());
      scope.registerFunction({
        name: setFiltersName,
        description:
          `Set one or more filters on the "${key}" list currently displayed on screen (${names.join(', ')}). ` +
          `This does NOT directly search or return rows — it only changes which rows the on-screen list ` +
          `shows; read the filtered rows from context.${key}.items afterwards. This call resolves once ` +
          `the list has finished reloading. The active filter values are in context.${key}.filters; pass ` +
          `null or an empty value to clear a filter.`,
        parameters: {
          type: 'object',
          // assemble the param schema from each filter descriptor; constrain to the declared filters
          properties: Object.fromEntries(Object.entries(cfg.filters!()).map(([name, f]) => [name, f.schema])),
          additionalProperties: false,
        },
        handler: async (rawArgs) => {
          const patch = (rawArgs ?? {}) as Record<string, unknown>;
          const descriptors = cfg.filters!();
          // apply only the filters whose value actually changes
          const changed = Object.keys(patch).filter(
            (name) => descriptors[name] && !valuesEqual(patch[name], descriptors[name].get())
          );
          if (changed.length === 0) return { success: true, applied: [] };
          // withhold the response until the resulting reload republishes context with the new filters
          holdUntilReload(setFiltersName);
          for (const name of changed) descriptors[name].set(patch[name]);
          return { success: true, applied: changed };
        },
      });
    }

    watch(
      [cfg.collection, cfg.page, cfg.pageCount, cfg.filterCount, cfg.totalCount],
      () => {
        const items = (cfg.collection.value ?? []).map((raw) => {
          const base = cfg.transformer ? cfg.transformer(raw) : raw;
          // attach a navigable link when one is configured and the item is an object
          if (cfg.link && base && typeof base === 'object') {
            return { ...base, link: cfg.link(raw) };
          }
          return base;
        });
        store.context[key as string] = {
          description: cfg.description(),
          // compact pipe-delimited table — column names once, not repeated per row like JSON
          items: formatCompactList(items),
          page: cfg.page.value,
          page_count: cfg.pageCount.value,
          filter_count: cfg.filterCount.value,
          total_count: cfg.totalCount.value,
          // current values of the autopilot-drivable filters, when the list declares any
          ...(cfg.filters
            ? {
                filters: Object.fromEntries(Object.entries(cfg.filters()).map(([name, f]) => [name, f.get()])),
              }
            : {}),
        };
        // release a held resolution only once the collection has actually reloaded (its reference
        // changes on fetch completion) — not on an intermediate page/count change. releasing lets the
        // state-sync gate flush this fresh context and get it acked before the withheld response goes out.
        if (releaseResolution && cfg.collection.value !== collectionAtHold) {
          releaseResolution();
          releaseResolution = null;
          collectionAtHold = undefined;
        }
      },
      { immediate: true }
    );
  }

  onUnmounted(() => {
    // release any in-flight hold so a pending call never strands past the page (idempotent)
    releaseResolution?.();
  });
}
